<script setup lang="ts">
import lodashIsNumber from 'lodash-es/isNumber';
import type { PropType } from 'vue';

import { DirectSaleComputedState, SellingMethod } from '~/apiClient';
import { type SaleItemEntity, isAuction, isDirectSale } from '~/types/sale.type';

// PROPS
const { sale } = defineProps({
    sale: { type: Object as PropType<SaleItemEntity>, required: true },
});

// COMPOSABLES
const { tn, tt } = useTypedI18n();
const { title, titleColor, titleAnnotation, titleAnnotationColor, priceColor, isFeeNoticeHidden } = isAuction(sale)
    ? useAuctionStatus(toRef(() => sale))
    : useDirectSaleStatus(toRef(() => sale));
const price = computed(() => (sale?.sellingMethod === SellingMethod.Auction ? sale.currentPrice : sale.price));
</script>

<template>
    <div class="flex flex-col gap-3 lg:gap-4">
        <template v-if="isDirectSale(sale) && sale.computedState === DirectSaleComputedState.NoActiveOrder && sale.buyNowPrice">
            <p class="text-xs font-extrabold tracking-widest uppercase lg:text-sm" :class="titleColor">
                {{ tt('sdp.DirectSale.directSaleStatus.buyNowPrice') }}
            </p>
            <div>
                <p class="text-3xl font-semibold lg:text-5xl" :class="priceColor">{{ tn(sale.buyNowPrice, 'currency') }}</p>
                <SaleFeesSimpleNotice v-if="!isFeeNoticeHidden" :sale="sale" />
            </div>
        </template>

        <template v-else>
            <p class="flex gap-3 text-xs font-extrabold tracking-widest uppercase lg:text-sm lg:max-xl:flex-col">
                <span class="inline-flex items-center gap-1" :class="titleColor">{{ title }}<AdpInfoModalButton /></span>
                <span v-if="titleAnnotation" :class="titleAnnotationColor">{{ titleAnnotation }}</span>
            </p>

            <div v-if="lodashIsNumber(price)">
                <p class="text-3xl font-semibold lg:text-5xl" :class="priceColor">{{ tn(price, 'currency') }}</p>
                <SaleFeesSimpleNotice v-if="!isFeeNoticeHidden" :sale="sale" />
            </div>
            <p v-else class="text-3xl font-semibold uppercase lg:text-5xl" :class="priceColor">*******</p>

            <template v-if="isAuction(sale)">
                <AuctionEstimatedMarketValue :auction="sale" />
                <AdpCountdown class="text-sm lg:text-base" :auction="sale" />
                <AuctionReservePriceNotice :auction="sale" class="self-start" />
            </template>
        </template>
    </div>
</template>
